import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { useRestaurant } from '../contexts/RestaurantContext';
const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  loading: true,
  isAuthenticated: false,
  isInitialized: false, // Add this to track initialization
};



const authReducer = (state, action) => {

  switch (action.type) {
    case 'LOGIN_SUCCESS':
      // console.log("Login Success Dispatch", action.payload);
      localStorage.setItem('access_token', action.payload.access_token);
      localStorage.setItem('refresh_token', action.payload.refresh_token);
      localStorage.setItem('admin_outlet_id', action.payload.user.outlet_id);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.access_token,
        refreshToken: action.payload.refresh_token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
      };
    case 'LOGOUT':
      // console.log("At Auth Context => ")
      const currentPath = window.location.pathname;

// Check if this is a public table route
  const isPublicTableRoute = 
  currentPath.includes('/table-order') || 
  currentPath.includes('/table-checkout');

      if (!sessionStorage.getItem("user") && !isPublicTableRoute) {
        console.log("Local Storage Admin Removed")
        // localStorage.setItem("Auth Context ", "YES");
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('admin_outlet_id');
        localStorage.removeItem('user_id');
      }

      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
        isInitialized: true,
      };
    case 'INITIALIZATION_COMPLETE':
      return {
        ...state,
        loading: false,
        isInitialized: true,
      };
    case 'TOKEN_REFRESH':
      localStorage.setItem('access_token', action.payload.access_token);
      localStorage.setItem('refresh_token', action.payload.refresh_token);
      localStorage.setItem('admin_outlet_id', action.payload.outlet_id);
      return {
        ...state,
        token: action.payload.access_token,
        refreshToken: action.payload.refresh_token,
        user: action.payload.user,
        isAuthenticated: true,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);


  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const refreshTokenValue = localStorage.getItem('refresh_token');

      // console.log('Initializing auth with token:', token ? 'exists' : 'none');

      // If no token exists, just mark initialization as complete
      if (!token) {
        // console.log('No token found, initialization complete');
        dispatch({ type: 'INITIALIZATION_COMPLETE' });
        return;
      }

      try {
        // console.log('Attempting to get current user with existing token');
        const user = await authService.getCurrentUser();
        // console.log('Successfully retrieved user:', user);
        dispatch({ type: 'SET_USER', payload: user });
      } catch (error) {
        console.error('Failed to get current user:', error);

        // Try to refresh the token if refresh token exists
        if (refreshTokenValue) {
          try {
            // console.log('Attempting to refresh token');
            const response = await authService.refreshToken(refreshTokenValue);
            // console.log('Token refreshed successfully');
            dispatch({ type: 'TOKEN_REFRESH', payload: response });

            // Retry getting user with new token
            // console.log('Retrying to get user with new token');
            const user = await authService.getCurrentUser();
            // console.log('Successfully retrieved user after token refresh');
            dispatch({ type: 'SET_USER', payload: user });
          } catch (refreshError) {
            console.error('Failed to refresh token:', refreshError);
            dispatch({ type: 'LOGOUT' });
            dispatch({ type: 'INITIALIZATION_COMPLETE' });
          }
        } else {
          // console.log('No refresh token available, logging out');
          dispatch({ type: 'LOGOUT' });
          dispatch({ type: 'INITIALIZATION_COMPLETE' });
        }
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    // const { setAdminLogin } = useRestaurant()
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      // console.log('AuthContext: Starting login process');

      const response = await authService.login(credentials);
      // console.log('AuthContext: Login API call successful', response);

      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
      // console.log('AuthContext: Login success dispatched');
      
      return { success: true, role: response.user.role };
    } catch (error) {
      console.error('AuthContext: Login failed', error);
      dispatch({ type: 'LOGIN_FAILURE' });

      const errorMessage = error.response?.data?.detail ||
        error.message ||
        'Login failed. Please check your credentials.';

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(refreshTokenValue);
      dispatch({ type: 'TOKEN_REFRESH', payload: response });
      return response.access_token;
    } catch (error) {
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  // Modified to be less aggressive - only clear tokens when explicitly needed
  const clearTokensForPublicPages = () => {
    const currentPath = window.location.pathname;
    const isPublicPage = currentPath === '/' ||
      currentPath.startsWith('/delivery') ||
      currentPath.startsWith('/takeaway') ||
      currentPath.startsWith('/catering') ||
      currentPath.startsWith('/table-reservations') ||
      currentPath.startsWith('/table-order');

    // Only clear tokens if we're actually on a public page AND user is authenticated
    // Don't clear during login process
    if (isPublicPage && state.isAuthenticated && !currentPath.includes('/admin/login')) {
      // console.log('Detected navigation to public page - clearing authentication tokens');
      dispatch({ type: 'LOGOUT' });
      return true; // Tokens were cleared
    }
    return false; // No tokens cleared
  };

  const value = {
    ...state,
    login,
    logout,
    refreshToken,
    clearTokensForPublicPages,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};